import { type SupabaseClient, createClient } from "@supabase/supabase-js";

export type AppRole = "admin" | "tenant";
export type TenancyStatus = "upcoming" | "active" | "ended";
export type ComplianceDocType = "CP12" | "CP12_BOILER_SERVICE" | "CP6" | "EPC" | "EICR";

// Live compliance event types exposed in admin UI. The legacy
// CP12_BOILER_SERVICE value stays in the union for back-compat with
// rows inserted before Batch 3c, but isn't surfaced in pickers — gas
// safety (CP12) and boiler service (CP6) are now distinct events that
// the cron auto-bundles into one job when armed against the same contractor.
export const COMPLIANCE_TYPES: ComplianceDocType[] = ["CP12", "CP6", "EICR", "EPC"];

export const COMPLIANCE_TYPE_LABEL: Record<ComplianceDocType, string> = {
  CP12: "Gas Safety Certificate (CP12)",
  CP12_BOILER_SERVICE: "Gas Safety + Boiler Service (legacy)",
  CP6: "Boiler Service (CP6)",
  EICR: "Electrical Installation Condition Report (EICR)",
  EPC: "Energy Performance Certificate (EPC)",
};

// Statutory + service intervals in months. Used by the upload form to
// auto-fill expiry and (Batch 3b) by the cert-extract agent as a sanity check.
export const COMPLIANCE_DURATION_MONTHS: Record<ComplianceDocType, number> = {
  CP12: 12,
  CP12_BOILER_SERVICE: 12,
  CP6: 12,
  EICR: 60,
  EPC: 120,
};

// Compliance capabilities a contractor can hold (kept distinct from `trades`
// so a Gas Safe + boiler-service engineer can be matched on cert work even
// when the trade list says they only cover Heating). Extensible - add CP6
// when a contractor needs it.
export const COMPLIANCE_CAPABILITIES = [
  "CP12",
  "CP12_BOILER_SERVICE",
  "CP6",
  "EICR",
  "EPC",
] as const;
export type ComplianceCapability = (typeof COMPLIANCE_CAPABILITIES)[number];

export const COMPLIANCE_CAPABILITY_LABEL: Record<ComplianceCapability, string> = {
  CP12: "Gas Safe (CP12)",
  CP12_BOILER_SERVICE: "Gas Safe + Boiler Service",
  CP6: "Boiler Service (CP6)",
  EICR: "EICR (NICEIC / NAPIT)",
  EPC: "EPC (DEA)",
};
export type RepairStatus =
  | "reported"
  | "triaged"
  | "contractor_assigned"
  | "in_progress"
  | "completed"
  | "cancelled";
export type RepairPriority = "low" | "medium" | "high" | "emergency";

export type Property = {
  id: string;
  display_name: string;
  line1: string;
  line2: string | null;
  postcode: string;
  notes: string | null;
};

export type Tenancy = {
  id: string;
  property_id: string;
  tenant_user_id: string;
  start_date: string;
  end_date: string | null;
  monthly_rent_pence: number;
  rent_due_day: number;
  deposit_amount_pence: number;
  rent_account_name: string;
  rent_sort_code: string;
  rent_account_number: string;
  rent_payment_reference: string;
  status: TenancyStatus;
};

export type ComplianceDocument = {
  id: string;
  property_id: string;
  type: ComplianceDocType;
  file_path: string;
  issue_date: string;
  expiry_date: string | null;
  uploaded_at: string;
};

export type TenantProfile = {
  user_id: string;
  full_name: string | null;
  phone: string | null;
  contact_email: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
};

/** Tenant-safe repair shape — matches `public.repairs_tenant` view. */
export type Repair = {
  id: string;
  tenancy_id: string;
  property_id: string;
  reporter_user_id: string;
  title: string;
  description: string;
  reporter_priority: RepairPriority;
  status: RepairStatus;
  triage_summary: string | null;
  triage_priority: RepairPriority | null;
  triaged_at: string | null;
  contractor_name: string | null;
  contractor_phone: string | null;
  contractor_email: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  completion_notes: string | null;
  created_at: string;
  updated_at: string;
};

/** Admin-only repair shape — adds internal_notes + external IDs. */
export type RepairAdmin = Repair & {
  external_triage_id: string | null;
  contractor_external_id: string | null;
  contractor_id: string | null;
  contractor_dispatched_at: string | null;
  primary_service: string | null;
  hmf_session_id: number | null;
  dispatch_brief: string | null;
  internal_notes: string | null;
};

export type RepairStatusEvent = {
  id: string;
  repair_id: string;
  status: RepairStatus;
  note: string | null;
  actor_user_id: string | null;
  created_at: string;
};

export const HMF_TRADES = [
  "Plumbing",
  "Heating",
  "Gas",
  "Electrical",
  "Multitrade",
  "Roofing",
  "Windows",
  "Locks",
  "Carpentry",
  "Drainage",
  "Air Conditioning",
  "Damp & Mould",
  "Alarms",
  "Appliances",
  "Support",
  "Oil",
  "Communal Heating",
  "Heat Pumps",
  "Cylinders",
] as const;
export type HmfTrade = (typeof HMF_TRADES)[number];

export type Contractor = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  trades: string[];
  compliance_capabilities: string[];
  notes: string | null;
  is_active: boolean;
  user_id: string | null;
  created_at: string;
  updated_at: string;
};

let cached: SupabaseClient | undefined;

export function getSupabase(): SupabaseClient {
  if (cached) return cached;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !key) {
    throw new Error("Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in environment.");
  }
  cached = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "implicit",
    },
  });
  return cached;
}

/** Test-only escape hatch to swap the cached client. */
export function __setSupabaseForTests(client: SupabaseClient | undefined) {
  cached = client;
}
